// filepath: src/pages/InventoryPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { socket } from "../services/socket";
import InventoryTable from "../components/admin/inventory/InventoryTable";
import InventoryGrid from "../components/admin/inventory/InventoryGrid";
import SearchInput from "../components/CommonComponents/SearchInput";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import Pagination from "../components/CommonComponents/Pagination";
import EmptyState from "../components/CommonComponents/EmptyState";
import TableLoadingSkeleton from "../components/CommonComponents/TableLoadingSkeleton";
import { adjustReleasedInventoryToBranch, getUnifiedInventory, releaseInventoryToBranch, updateInventory, updateRawMaterialInventory } from "../services/inventoryServices";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Package } from "lucide-react";
import EditStockModal from "../components/admin/inventory/EditStockModal";

const BRANCH_BADGE_COLORS = [
    { background: "#fee2e2", color: "#991b1b", border: "#fecaca" },
    { background: "#ffedd5", color: "#9a3412", border: "#fed7aa" },
    { background: "#fef9c3", color: "#854d0e", border: "#fef08a" },
    { background: "#dcfce7", color: "#166534", border: "#bbf7d0" },
    { background: "#cffafe", color: "#155e75", border: "#a5f3fc" },
    { background: "#dbeafe", color: "#1e3a8a", border: "#bfdbfe" },
    { background: "#ede9fe", color: "#5b21b6", border: "#ddd6fe" },
    { background: "#fce7f3", color: "#9d174d", border: "#fbcfe8" },
    { background: "#f3e8ff", color: "#6b21a8", border: "#e9d5ff" },
    { background: "#e5e7eb", color: "#1f2937", border: "#d1d5db" },
];

const InventoryPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [inventories, setInventories] = useState([]);
    const [activeBranchTab, setActiveBranchTab] = useState("");
    const [branches, setBranches] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState("PRODUCT");
    const [grocerySort, setGrocerySort] = useState("NONE");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("table"); // table or grid
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const inventoriesPerPage = 10;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`; // match ProductList
    const lastAppliedQueryRef = useRef("");

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryBranchId = queryParams.get("branchId");
    const queryProductId = queryParams.get("productId");
    const queryProductName = queryParams.get("productName");
    const queryTs = queryParams.get("ts");
    const shouldAutoFocusSearch = Boolean(queryProductId || queryProductName);

    // Fetch inventory
    const loadInventory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getUnifiedInventory(token);
            setInventories(data || []);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const loadBranches = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/branches`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = Array.isArray(response.data?.data) ? response.data.data : [];
            const activeBranches = data.filter((branch) => String(branch?.status || "ACTIVE") === "ACTIVE");
            setBranches(activeBranches);
        } catch (err) {
            console.error("Failed to load branches:", err);
            setBranches([]);
        }
    }, [token]);

    // Handler for adjusting inventory
    const handleAdjust = async (inventory, change) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            if (inventory?.raw_material_id) {
                await updateRawMaterialInventory(inventory.raw_material_id, change, token);
            } else if (inventory?.product_id) {
                await updateInventory(inventory.product_id, change, token, inventory.branch_id ?? null);
            } else {
                throw new Error("Invalid inventory item.");
            }
            showSuccessToast(`Inventory ${change > 0 ? "restocked" : "deducted"}`);
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    };

    const branchTabs = useMemo(
        () => branches.map((branch) => ({ key: `branch-${branch.id}`, label: branch.name })),
        [branches]
    );

    const selectedBranchId = useMemo(() => {
        if (!activeBranchTab || !String(activeBranchTab).startsWith("branch-")) return null;
        const value = Number(String(activeBranchTab).replace("branch-", ""));
        return Number.isFinite(value) ? value : null;
    }, [activeBranchTab]);
    const selectedBranchName = useMemo(() => {
        if (!selectedBranchId) return "";
        const branch = branches.find((item) => Number(item.id) === Number(selectedBranchId));
        return branch?.name || "";
    }, [branches, selectedBranchId]);

    const selectedBranchBadgeStyle = useMemo(() => {
        if (!selectedBranchName) return undefined;
        const colorIndex = selectedBranchId
            ? Math.abs(Number(selectedBranchId) - 1) % BRANCH_BADGE_COLORS.length
            : 0;
        const selected = BRANCH_BADGE_COLORS[colorIndex];
        return {
            background: selected.background,
            color: selected.color,
            border: `1px solid ${selected.border}`,
        };
    }, [selectedBranchId, selectedBranchName]);

    const displayedInventory = useMemo(() => {
        const adminRows = inventories.filter((inv) => inv.branch_id === null || inv.branch_id === undefined);

        if (!selectedBranchId) {
            return adminRows.map((inv) => ({ ...inv, released_quantity: 0 }));
        }

        const branchRows = inventories.filter((inv) => Number(inv.branch_id) === selectedBranchId);
        const branchQtyByKey = new Map();

        branchRows.forEach((inv) => {
            const key = inv.product_id
                ? `product:${inv.product_id}`
                : inv.raw_material_id
                    ? `raw:${inv.raw_material_id}`
                    : null;
            if (!key) return;
            branchQtyByKey.set(key, Number(inv.quantity) || 0);
        });

        return adminRows.map((inv) => {
            const key = inv.product_id
                ? `product:${inv.product_id}`
                : inv.raw_material_id
                    ? `raw:${inv.raw_material_id}`
                    : null;
            const hasBranchRecord = key ? branchQtyByKey.has(key) : false;
            return {
                ...inv,
                released_quantity: hasBranchRecord ? (branchQtyByKey.get(key) || 0) : null,
            };
        });
    }, [inventories, selectedBranchId]);

    // Search + type filter (Products / Raw Materials)
    const baseFilteredInventory = displayedInventory.filter((inv) => {
        const name = (inv.product_name || inv.name || inv.raw_material_name || "").toLowerCase();
        const unit = String(inv.unit || inv.unit_type || "").toLowerCase();
        const type = String(inv.type || "").toLowerCase();
        const query = searchTerm.trim().toLowerCase();

        const matchesSearch = !query || name.includes(query) || unit.includes(query) || type.includes(query);
        const isProduct = Boolean(inv.product_id);
        const isRawMaterial = Boolean(inv.raw_material_id);
        const matchesType =
            (typeFilter === "PRODUCT" && isProduct) ||
            (typeFilter === "RAW_MATERIAL" && isRawMaterial);

        return matchesSearch && matchesType;
    });
    const filteredInventory = useMemo(() => {
        if (typeFilter !== "PRODUCT" || grocerySort === "NONE") {
            return baseFilteredInventory;
        }

        const isGrocery = (inv) =>
            String(inv.category || "").trim().toLowerCase() === "grocery product";

        const groceries = baseFilteredInventory.filter(isGrocery);
        const sortedGroceries = [...groceries].sort((a, b) => {
            const aQty = Number(a.released_quantity) || 0;
            const bQty = Number(b.released_quantity) || 0;
            return grocerySort === "HIGH_TO_LOW" ? bQty - aQty : aQty - bQty;
        });

        return sortedGroceries;
    }, [baseFilteredInventory, typeFilter, grocerySort]);
    const totalPages = Math.max(Math.ceil(filteredInventory.length / inventoriesPerPage), 1);
    const currentInventory = filteredInventory.slice(
        (currentPage - 1) * inventoriesPerPage,
        currentPage * inventoriesPerPage
    );

    useEffect(() => {
        loadInventory();
        loadBranches();
    }, [loadInventory, loadBranches]);

    useEffect(() => {
        const key = `${queryBranchId || ""}|${queryProductId || ""}|${queryProductName || ""}|${queryTs || ""}`;
        if (!key || key === "||") return;
        if (lastAppliedQueryRef.current === key) return;

        const hasBranch = queryBranchId && String(queryBranchId).trim() !== "";
        const hasProductName = queryProductName && String(queryProductName).trim() !== "";
        let applied = false;

        if (hasProductName) {
            setSearchTerm(String(queryProductName));
            applied = true;
        }

        if (hasBranch) {
            setActiveBranchTab(`branch-${queryBranchId}`);
            applied = true;
        }

        if (applied) {
            lastAppliedQueryRef.current = key;
            // Clear URL params so search bar can be edited/cleared without being re-applied
            navigate(location.pathname, { replace: true });
        }
    }, [queryBranchId, queryProductId, queryProductName, queryTs, location.pathname, navigate]);

    useEffect(() => {
        const key = `${queryBranchId || ""}|${queryProductId || ""}|${queryProductName || ""}|${queryTs || ""}`;
        if (!key || key === "|||") {
            if (!searchTerm) {
                lastAppliedQueryRef.current = "";
            }
        }
    }, [queryBranchId, queryProductId, queryProductName, queryTs, searchTerm]);
    // Listen for products_updated event to reload inventory
    useEffect(() => {
        if (!socket) return;
        const handler = () => loadInventory();
        socket.on("products_updated", handler);
        return () => {
            socket.off("products_updated", handler);
        };
    }, [socket, loadInventory]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [filteredInventory, totalPages, currentPage]);
    useEffect(() => {
        if (!branchTabs.length) {
            setActiveBranchTab("");
            return;
        }

        const queryTabKey = queryBranchId ? `branch-${queryBranchId}` : null;
        if (queryTabKey && branchTabs.some((tab) => tab.key === queryTabKey)) {
            if (activeBranchTab !== queryTabKey) {
                setActiveBranchTab(queryTabKey);
            }
            return;
        }

        if (!activeBranchTab || !branchTabs.some((tab) => tab.key === activeBranchTab)) {
            setActiveBranchTab(branchTabs[0].key);
        }
    }, [branchTabs, activeBranchTab, queryBranchId]);

    useEffect(() => {
        const key = `${queryBranchId || ""}|${queryProductId || ""}|${queryProductName || ""}|${queryTs || ""}`;
        if (!key || key === "|||") return;
        if (lastAppliedQueryRef.current === key) return;
        if (searchTerm) return;
        if (!queryProductId) return;
        if (!Array.isArray(inventories) || inventories.length === 0) return;
        const match = inventories.find(inv => String(inv.product_id) === String(queryProductId));
        if (match?.name) {
            setSearchTerm(match.name);
            lastAppliedQueryRef.current = key;
            navigate(location.pathname, { replace: true });
        }
    }, [inventories, queryBranchId, queryProductId, queryProductName, queryTs, searchTerm, location.pathname, navigate]);

    // Handler for saving stock from modal
    const handleEditStock = async (inventory, newQuantity) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            if (inventory?.product_id && !Number.isInteger(Number(newQuantity))) {
                showErrorToast("Product stock must be a whole number.");
                return;
            }
            const quantityChange = newQuantity - inventory.quantity;
            if (quantityChange === 0) return; // No change
            if (inventory?.raw_material_id) {
                await updateRawMaterialInventory(inventory.raw_material_id, quantityChange, token);
            } else if (inventory?.product_id) {
                await updateInventory(inventory.product_id, quantityChange, token, inventory.branch_id ?? null);
            } else {
                throw new Error("Invalid inventory item.");
            }
            showSuccessToast("Inventory updated");
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    };

    // Handler for modal save
    const handleSaveStock = (newQuantity) => {
        if (selectedInventory) {
            handleEditStock(selectedInventory, newQuantity);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleReleaseToBranch = async (inventory, qty, remarks) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        if (!selectedBranchId) return showErrorToast("Select a branch first.");
        try {
            const payload = {
                branch_id: selectedBranchId,
                qty: Number(qty),
                remarks: remarks || "",
            };
            if (inventory?.product_id) {
                payload.product_id = inventory.product_id;
            } else if (inventory?.raw_material_id) {
                payload.raw_material_id = inventory.raw_material_id;
            } else {
                throw new Error("Invalid inventory item.");
            }
            await releaseInventoryToBranch(payload, token);
            showSuccessToast("Stock released to branch.");
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err?.response?.data?.message || err.message || "Failed to release stock.");
        }
    };

    const handleEditReleasedStock = async (inventory, newReleasedQty) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        if (!selectedBranchId) return showErrorToast("Select a branch first.");
        try {
            const parsed = Number(newReleasedQty);
            if (!Number.isFinite(parsed) || parsed < 0) {
                showErrorToast("Released quantity must be 0 or greater.");
                return;
            }
            if (inventory?.product_id && !Number.isInteger(parsed)) {
                showErrorToast("Product released stock must be a whole number.");
                return;
            }

            const payload = {
                branch_id: selectedBranchId,
                released_qty: parsed,
            };

            if (inventory?.product_id) {
                payload.product_id = inventory.product_id;
            } else if (inventory?.raw_material_id) {
                payload.raw_material_id = inventory.raw_material_id;
            } else {
                throw new Error("Invalid inventory item.");
            }

            await adjustReleasedInventoryToBranch(payload, token);
            showSuccessToast("Released stock updated.");
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err?.response?.data?.message || err.message || "Failed to update released stock.");
        }
    };

    const inventorySkeletonConfig = useMemo(() => {
        if (typeFilter === "RAW_MATERIAL") {
            return {
                columnLabels: [
                    "Image",
                    "Name",
                    "Type",
                    "Unit",
                    "Stocks Available",
                    "Stocks Released",
                    "Actions",
                ],
                columnTypes: ["image", "text", "text", "text", "text", "text", "actions"],
                columnTemplate: "56px 1.8fr 0.9fr 0.9fr 1.2fr 1.2fr 1fr",
            };
        }
        return {
            columnLabels: [
                "Image",
                "Name",
                "Category",
                "Type",
                "Unit",
                "Stocks Available",
                "Stocks Released",
                "Actions",
            ],
            columnTypes: ["image", "text", "text", "text", "text", "text", "text", "actions"],
            columnTemplate: "56px 1.6fr 1.2fr 0.9fr 0.9fr 1.2fr 1.2fr 1fr",
        };
    }, [typeFilter]);

    return (
        <div className="pt-6">
            <div className="flex flex-col">
                {/* Branch switcher tabs */}
                <div className="mb-4">
                    <p className="text-sm font-semibold mb-2" style={{ color: "var(--color-text)" }}>Branches</p>
                    <div className="flex gap-1 flex-wrap">
                    {branchTabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`px-3 py-1 rounded text-sm font-medium focus:outline-none transition-colors duration-100
                                ${activeBranchTab === tab.key
                                    ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)] shadow-md'
                                    : 'bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)]'}
                            `}
                            style={{
                                minWidth: 90,
                                fontWeight: activeBranchTab === tab.key ? 700 : 500,
                                boxShadow: activeBranchTab === tab.key ? '0 2px 8px 0 rgba(0,0,0,0.08)' : undefined,
                                border: activeBranchTab === tab.key ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                            }}
                            onClick={() => { setActiveBranchTab(tab.key); setCurrentPage(1); }}
                        >
                            {tab.label}
                        </button>
                    ))}
                    </div>
                </div>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <p>
                        <span className="font-semibold">Branch:</span>{" "}
                        <span
                            className={`inline-flex items-center rounded px-2 py-0.5 font-semibold ${selectedBranchName
                                ? "shadow-sm"
                                : "bg-gray-100 text-gray-600 border border-gray-200"
                                }`}
                            style={selectedBranchName ? selectedBranchBadgeStyle : undefined}
                        >
                            {selectedBranchName || "Not selected"}
                        </span>
                    </p>
                    <p><span className="font-semibold">Stocks Available</span> = admin central stock</p>
                    <p><span className="font-semibold">Stocks Released</span> = released quantity to selected branch</p>
                </div>
                {/* Search + Add Button (no add for inventory, so just search) */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <SearchInput
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search by name, type, or unit..."
                        disabled={loading}
                        autoFocus={shouldAutoFocusSearch}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-600">Type:</span>
                        <button
                            type="button"
                            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${typeFilter === "PRODUCT"
                                ? "bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                }`}
                            onClick={() => {
                                setTypeFilter("PRODUCT");
                                setCurrentPage(1);
                            }}
                            disabled={loading}
                        >
                            Products
                        </button>
                        <button
                            type="button"
                            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${typeFilter === "RAW_MATERIAL"
                                ? "bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                }`}
                            onClick={() => {
                                setTypeFilter("RAW_MATERIAL");
                                setCurrentPage(1);
                            }}
                            disabled={loading}
                        >
                            Raw Materials
                        </button>
                        {typeFilter === "PRODUCT" && (
                            <div className="flex items-center gap-2 ml-2 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-600">Grocery Sort:</span>
                                <select
                                    value={grocerySort}
                                    onChange={(e) => {
                                        setGrocerySort(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700"
                                    disabled={loading}
                                >
                                    <option value="NONE">Default</option>
                                    <option value="HIGH_TO_LOW">High to Low</option>
                                    <option value="LOW_TO_HIGH">Low to High</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>
                {/* Header + View Toggle */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Inventory List</h2>
                    <ViewToggle
                        viewMode={viewMode}
                        onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                    />
                </div>
                {error ? (
                    <EmptyState
                        icon={Package}
                        title="Failed to Load Inventory"
                        message="Check your internet connection and try again."
                        onRetry={loadInventory}
                        retryLabel="Reload"
                    />
                ) : loading ? (
                    <div className="rounded-lg border border-gray-200 bg-white overflow-auto max-h-[calc(100vh-340px)] min-h-[300px]">
                        <TableLoadingSkeleton
                            mode={viewMode}
                            columnLabels={inventorySkeletonConfig.columnLabels}
                            columnTypes={inventorySkeletonConfig.columnTypes}
                            rowCount={4}
                            gridCount={8}
                            columnTemplate={inventorySkeletonConfig.columnTemplate}
                        />
                    </div>
                ) : currentInventory.length === 0 ? (
                    <EmptyState
                        icon={Package}
                        title="No Inventory Found"
                        message={searchTerm
                            ? "No inventory matches the selected filters."
                            : "No inventory available yet."}
                    />
                ) : viewMode === "table" ? (
                    <InventoryTable
                        inventories={currentInventory}
                        uploadsBaseURL={uploadsBaseURL}
                        onEditStock={handleEditStock}
                        onEditReleasedStock={handleEditReleasedStock}
                        onReleaseToBranch={handleReleaseToBranch}
                        selectedBranchName={selectedBranchName}
                        canRelease={Boolean(selectedBranchId)}
                        typeFilter={typeFilter}
                    />
                ) : (
                    <InventoryGrid
                        inventories={currentInventory}
                        onAdjust={handleAdjust}
                        uploadsBaseURL={uploadsBaseURL}
                        onEdit={inv => {
                            setSelectedInventory(inv);
                            setEditModalOpen(true);
                        }}
                    />
                )}
                {/* Pagination - Only show when no error and data exists */}
                {!error && (
                    <div className="flex flex-col items-center mb-12 mt-2">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                        <p className="text-sm text-gray-600 mt-2">
                            Showing {filteredInventory.length === 0 ? 0 : (currentPage - 1) * inventoriesPerPage + 1}
                            – {Math.min(currentPage * inventoriesPerPage, filteredInventory.length)}{' '}
                            of {filteredInventory.length} items | Page {currentPage} of {totalPages}
                        </p>
                    </div>
                )}
            </div>
            {/* Edit Stock Modal for Grid */}
            <EditStockModal
                isOpen={editModalOpen}
                onClose={handleCloseModal}
                inventory={selectedInventory}
                onSave={handleSaveStock}
            />
        </div>
    );
};

export default InventoryPage;
